import { IOffersDataState } from '../../types/offersData';
import { IThunkActionCreator } from '../../types/thunk';
import { resolveOffersData } from '../../api/offersData';

import { setOffersData } from './setOffersData';
import { updateBrowserUrl } from './updateBrowserUrl';
import { ISetLocationPageParams, setPage } from '../location';
import { trackingActionUpdateListing, trackingActionShowSimilarJK, trackPromoSnippetShow } from '../analytics';
import { changePreloaderVisibility } from '../preloader';
import { getSeparateUrl, memoize } from '@cian/newbuilding-utils';
import { cleanOffersCount } from './setOffersCount';
import { setProducts } from '../analytics/actions';
import { prepareProducts } from '../../mappers';
import { getPromoSnippetVisibility } from '../../utils/promoSnippet';
import { setPromoSnippetVisibility } from '../promoSnippet';
import { setQuickLinks } from '../quickLinks';

const SCROLL_TO_TOP_BEFORE_SET_OFFERS_DATA_REASONS = [
  'pagination',
  'geo_filter',
  'room_filter',
  'quick_price_filter',
  'construction_status_filter',
  'more_filters_submit',
  'delete_filter_tag',
  'clear_all_filter_tags',
  'location_modal_submit',
  'manifestation_type_filter',
  'quick_manifestation_type_filter',
];

export interface IGetOffersDataParams extends ISetLocationPageParams {
  reason?: string;
  shouldUpdateHistory?: boolean;
  trackingAction?(queryString: string): IThunkActionCreator;
  path?: string;
  page?: number;
}

const defaultParams = {
  shouldUpdateHistory: true,
};
const getSeparateUrlMemo = memoize(getSeparateUrl);
export const isNewDomain = memoize((fullUrl: string) => {
  if (typeof window === 'undefined' || !fullUrl) {
    return false;
  }

  const { domain: newDomain } = getSeparateUrlMemo(fullUrl);
  const { domain } = getSeparateUrlMemo(window.location.href);
  const isLocalDev = domain.indexOf('localhost') !== -1;

  return !isLocalDev && domain !== newDomain;
});
export const getOffersData = (params: IGetOffersDataParams = {}): IThunkActionCreator => {
  return (dispatch, getState, context) => {
    const { location, jsonQuery, config } = getState();
    const {
      trackingAction,
      path = location.path,
      page = 1,
      reason = 'unknown',
      shouldUpdateHistory,
    } = {
      ...defaultParams,
      ...params,
    };
    dispatch(changePreloaderVisibility(true));

    scrollToTopBeforeSetOffersData(reason);

    return resolveOffersData(context.httpApi, jsonQuery, path, location.subdomain, page)
      .then((offersData: IOffersDataState) => {
        dispatch(setPage(page, offersData, jsonQuery));

        if (shouldUpdateHistory) {
          dispatch(updateBrowserUrl(offersData));
        }

        if (isNewDomain(offersData.fullUrl)) {
          return;
        }

        dispatch(setOffersData(offersData));

        const newPath = new URL(offersData.fullUrl).pathname;
        const isLinksRelevant = newPath === path;
        if (!isLinksRelevant) {
          dispatch(setQuickLinks([]));
        }

        dispatch(setProducts(prepareProducts(offersData)));
        dispatch(trackingActionUpdateListing());

        if (offersData.newbuildingPromoSnippet) {
          dispatch(trackPromoSnippetShow(offersData.newbuildingPromoSnippet.newbuilding.id, jsonQuery));
        }

        if (trackingAction) {
          dispatch(trackingAction(offersData.queryString));
        }
        if (offersData.similarNewbuildings && offersData.similarNewbuildings.length) {
          dispatch(trackingActionShowSimilarJK(jsonQuery));
        }

        const isPromoSnippetVisible =
          config.isPromoSnippetEnabled &&
          offersData.newbuildingPromoSnippet !== null &&
          getPromoSnippetVisibility(page);

        dispatch(setPromoSnippetVisibility(isPromoSnippetVisible));

        dispatch(changePreloaderVisibility(false));
        dispatch(cleanOffersCount());
      })
      .catch(() => {
        dispatch(changePreloaderVisibility(false));
      });
  };
};

function scrollToTopBeforeSetOffersData(reason: string) {
  if (typeof window !== 'undefined' && SCROLL_TO_TOP_BEFORE_SET_OFFERS_DATA_REASONS.includes(reason)) {
    window.scrollTo({ top: 0 });
  }
}
